import { useMemo, useRef, useState } from "react";
import { Plus, ChevronDown } from "lucide-react";
import {
  CONDITION_CATEGORIES,
  ACCENT_CLASS,
  categoryOfCondition,
  categoryByIdMap,
} from "@/constants/conditionCategories";
import { createDefaultCondition, uid } from "./segmentFormUtils";
import {
  MEMBERSHIP_CATEGORIES,
  membershipFields,
  tagOpForCategory,
  makeTagCondition,
} from "./conditionModel";
import CategoryCard from "./CategoryCard";

function Segmented({ value, onChange, options }) {
  return (
    <div className="inline-flex rounded-(--radius-md) bg-(--surface-2) p-0.5">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={`rounded-[calc(var(--radius-md)-2px)] px-3 py-1.5 text-sm font-medium transition-colors ${
            value === opt.value
              ? "bg-(--surface-1) text-(--text-primary) shadow-sm"
              : "text-(--text-secondary) hover:text-(--text-primary)"
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function AddGroupMenu({ available, onPick }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  if (available.length === 0) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        className="inline-flex w-full items-center justify-center gap-1.5 rounded-(--radius-lg) border border-dashed border-(--border-subtle) px-3 py-2.5 text-sm font-medium text-(--brand-primary) hover:bg-(--surface-2)"
      >
        <Plus size={14} /> Add condition group <ChevronDown size={14} />
      </button>
      {open && (
        <div className="absolute bottom-full left-1/2 z-10 mb-1 w-56 -translate-x-1/2 overflow-hidden rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-1) py-1 shadow-lg">
          {available.map((category) => {
            const Icon = category.icon;
            return (
              <button
                key={category.id}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  onPick(category.id);
                  setOpen(false);
                }}
                className="flex w-full items-center gap-2.5 px-3 py-2 text-sm text-(--text-primary) hover:bg-(--surface-2)"
              >
                <span
                  className={`flex h-6 w-6 items-center justify-center rounded-(--radius-sm) ${
                    ACCENT_CLASS[category.accent] ?? ACCENT_CLASS.slate
                  }`}
                >
                  <Icon size={13} strokeWidth={2} />
                </span>
                {category.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

const connectorLabel = (activeTab, combinator) =>
  activeTab === "exclude" ? "OR" : combinator === "and" ? "AND" : "OR";

export default function BuildConditions({
  definition,
  onSetGroup,
  fieldOptionConfig,
  config,
}) {
  // When `config.categories` is provided it REPLACES the segment wizard's
  // CONDITION_CATEGORIES (drives the add-group menu, cards, and routing). Absent
  // => the exact prior behavior. `includeOnly`/`lockCombinator` hide the
  // Include/Exclude and AND/OR controls and pin their values.
  const activeCategories = config?.categories ?? CONDITION_CATEGORIES;
  const categoryById = useMemo(
    () => categoryByIdMap(activeCategories),
    [activeCategories],
  );
  const includeOnly = config?.includeOnly ?? false;
  const lockedCombinator = config?.lockCombinator;

  const [activeTab, setActiveTab] = useState("include");
  const [combinator, setCombinator] = useState(() => {
    if (lockedCombinator) return lockedCombinator;
    return (definition.any?.length ?? 0) > 0 &&
      (definition.all?.length ?? 0) === 0
      ? "or"
      : "and";
  });

  const effectiveTab = includeOnly ? "include" : activeTab;
  const effectiveCombinator = lockedCombinator ?? combinator;

  const includeGroup = effectiveCombinator === "and" ? "all" : "any";
  const group = effectiveTab === "exclude" ? "none" : includeGroup;
  const conditions = definition[group] ?? [];

  const { byCategory, orderedIds } = useMemo(() => {
    const map = {};
    const order = [];
    for (const cond of conditions) {
      const cat = categoryOfCondition(cond, activeCategories);
      if (!map[cat]) {
        map[cat] = [];
        order.push(cat);
      }
      map[cat].push(cond);
    }
    return { byCategory: map, orderedIds: order };
  }, [conditions, activeCategories]);

  const presentCategories = orderedIds
    .map((id) => categoryById[id])
    .filter(Boolean);
  const availableCategories = activeCategories.filter((c) => !byCategory[c.id]);

  const handleCombinatorChange = (next) => {
    if (next === effectiveCombinator) return;
    const from = effectiveCombinator === "and" ? "all" : "any";
    const to = next === "and" ? "all" : "any";
    const moved = (definition[from] ?? []).map((cond) =>
      cond.field === "tags"
        ? {
            ...cond,
            op: tagOpForCategory(
              categoryOfCondition(cond, activeCategories),
              next,
              "include",
            ),
          }
        : cond,
    );
    onSetGroup(from, []);
    onSetGroup(to, [...(definition[to] ?? []), ...moved]);
    setCombinator(next);
  };

  const setCategoryConditions = (categoryId, nextForCategory) => {
    const result = [];
    let inserted = false;
    for (const c of conditions) {
      if (categoryOfCondition(c, activeCategories) === categoryId) {
        if (!inserted) {
          result.push(...nextForCategory);
          inserted = true;
        }
      } else {
        result.push(c);
      }
    }
    if (!inserted) result.push(...nextForCategory);
    onSetGroup(group, result);
  };

  const addCategory = (categoryId) => {
    const category = categoryById[categoryId];
    // The tag / membership / behavior special-cases below are keyed by the
    // segment wizard's category ids (MEMBERSHIP_CATEGORIES, "behavior") and its
    // "tags" kind. A replaceable analytics category set doesn't declare those
    // kinds — its categories are plain field pickers — so we only take the
    // special-case paths for the default segment category set.
    const usesSegmentSpecialCases = !config?.categories;
    let seed;
    if (category.kind === "tags") {
      seed = [
        makeTagCondition(
          categoryId,
          tagOpForCategory(categoryId, effectiveCombinator, effectiveTab),
        ),
      ];
    } else if (
      usesSegmentSpecialCases &&
      MEMBERSHIP_CATEGORIES.has(categoryId)
    ) {
      const first = membershipFields(categoryId, fieldOptionConfig)[0];
      seed = [
        {
          id: uid(),
          field: first.field,
          op: first.op,
          value: [],
          disabled: false,
        },
      ];
    } else if (usesSegmentSpecialCases && categoryId === "behavior") {
      seed = [
        {
          id: uid(),
          field: "sessionDuration",
          op: "gte",
          value: "",
          disabled: false,
        },
      ];
    } else if (category.fieldDescriptors) {
      // Analytics category: seed one empty `in` condition on the first field so
      // the (condition-derived) card appears. AnalyticsCategoryBody renders every
      // field of the category and manages values from here; empty conditions are
      // a backend no-op. Does NOT go through createDefaultCondition/SEGMENT_FIELDS.
      seed = [
        {
          id: uid(),
          field: category.fields[0],
          op: "in",
          value: [],
          disabled: false,
        },
      ];
    } else {
      seed = [createDefaultCondition(category.fields[0], fieldOptionConfig)];
    }
    onSetGroup(group, [...conditions, ...seed]);
  };

  const removeCategory = (categoryId) => {
    onSetGroup(
      group,
      conditions.filter(
        (c) => categoryOfCondition(c, activeCategories) !== categoryId,
      ),
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-(--text-primary)">
            Build conditions
          </h3>
          <p className="mt-0.5 text-sm text-(--text-secondary)">
            {effectiveTab === "exclude"
              ? "Exclude viewers who match any of these conditions."
              : "Include viewers that match the following conditions."}
          </p>
        </div>
        {!includeOnly && (
          <Segmented
            value={activeTab}
            onChange={setActiveTab}
            options={[
              { value: "include", label: "Include" },
              { value: "exclude", label: "Exclude" },
            ]}
          />
        )}
      </div>

      {!lockedCombinator && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          {effectiveTab === "include" ? (
            <Segmented
              value={effectiveCombinator}
              onChange={handleCombinatorChange}
              options={[
                { value: "and", label: "Match ALL (AND)" },
                { value: "or", label: "Match ANY (OR)" },
              ]}
            />
          ) : (
            <span className="text-xs text-(--text-muted)">
              A viewer is excluded if any condition below matches.
            </span>
          )}
        </div>
      )}

      {presentCategories.length === 0 ? (
        <div className="rounded-(--radius-lg) border border-dashed border-(--border-subtle) px-4 py-10 text-center">
          <p className="text-sm text-(--text-secondary)">
            No conditions yet. Add a condition group to start targeting.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {presentCategories.map((category, index) => (
            <div key={category.id} className="relative">
              {index > 0 && (
                <span className="absolute -top-1.5 left-4 z-10 -translate-y-1/2 rounded-(--radius-pill) bg-(--brand-primary) px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-(--brand-primary-contrast) shadow-sm ring-4 ring-(--surface-1)">
                  {connectorLabel(effectiveTab, effectiveCombinator)}
                </span>
              )}
              <CategoryCard
                category={category}
                conditions={byCategory[category.id]}
                combinator={effectiveCombinator}
                activeTab={effectiveTab}
                fieldOptionConfig={fieldOptionConfig}
                onSetConditions={(next) =>
                  setCategoryConditions(category.id, next)
                }
                onRemoveCategory={() => removeCategory(category.id)}
              />
            </div>
          ))}
        </div>
      )}

      <AddGroupMenu available={availableCategories} onPick={addCategory} />
    </div>
  );
}
